export const TEF_SCORE_SCALE = [
  {
    nclc: "10+",
    cecrl: "C2",
    co: { min: 349, max: 360 },
    ce: { min: 298, max: 300 },
    ee: { min: 416, max: 450 }
  },
  {
    nclc: "9",
    cecrl: "C1",
    co: { min: 298, max: 348 },
    ce: { min: 263, max: 297 },
    ee: { min: 371, max: 415 }
  },
  {
    nclc: "8",
    cecrl: "C1",
    co: { min: 280, max: 297 },
    ce: { min: 248, max: 262 },
    ee: { min: 349, max: 370 }
  },
  {
    nclc: "7",
    cecrl: "B2",
    co: { min: 249, max: 279 },
    ce: { min: 207, max: 247 },
    ee: { min: 310, max: 348 }
  },
  {
    nclc: "6",
    cecrl: "B2",
    co: { min: 217, max: 248 },
    ce: { min: 181, max: 206 },
    ee: { min: 271, max: 309 }
  },
  {
    nclc: "5",
    cecrl: "B1",
    co: { min: 181, max: 216 },
    ce: { min: 151, max: 180 },
    ee: { min: 226, max: 270 }
  },
  {
    nclc: "4",
    cecrl: "B1",
    co: { min: 145, max: 180 },
    ce: { min: 121, max: 150 },
    ee: { min: 181, max: 225 }
  },
  {
    nclc: "3",
    cecrl: "A2",
    co: { min: 116, max: 144 },
    ce: { min: 97, max: 120 },
    ee: { min: 145, max: 180 }
  }
];

export function getCompetencyLabel(competency: string): string {
  if (!competency) return "";
  const key = competency.trim().toLowerCase();
  if (key === "reading" || key === "ce" || key === "comprehension ecrite" || key === "compréhension écrite (ce)") {
    return "Compréhension Écrite (CE)";
  }
  if (key === "listening" || key === "co" || key === "comprehension orale" || key === "compréhension orale (co)") {
    return "Compréhension Orale (CO)";
  }
  if (key === "writing" || key === "ee" || key === "expression ecrite" || key === "expression écrite (ee)") {
    return "Expression Écrite (EE)";
  }
  if (key === "speaking" || key === "eo" || key === "expression orale" || key === "expression orale (eo)") {
    return "Expression Orale (EO)";
  }
  return competency;
}

export function getCompetencyAbbreviation(competency: string): string {
  if (!competency) return "";
  const key = competency.trim().toLowerCase();
  if (key === "reading" || key === "ce" || key === "comprehension ecrite" || key === "compréhension écrite (ce)") {
    return "CE";
  }
  if (key === "listening" || key === "co" || key === "comprehension orale" || key === "compréhension orale (co)") {
    return "CO";
  }
  if (key === "writing" || key === "ee" || key === "expression ecrite" || key === "expression écrite (ee)") {
    return "EE";
  }
  if (key === "speaking" || key === "eo" || key === "expression orale" || key === "expression orale (eo)") {
    return "EO";
  }
  return competency;
}

export interface TefLevel {
  nclc: string;
  cecrl: string;
  range: string;
  label: string;
}

export function getTefLevelByScore(competency: string, score: number | string): TefLevel | null {
  if (score === undefined || score === null) return null;
  
  // Extract number if it's "230/300" etc.
  let parsedScore = typeof score === "string" ? parseInt(score.split("/")[0].trim()) : score;
  if (isNaN(parsedScore)) return null;

  const key = competency.trim().toLowerCase();
  let subKey: "co" | "ce" | "ee";
  if (key === "listening" || key === "co" || key === "comprehension orale" || key === "compréhension orale (co)") {
    subKey = "co";
  } else if (key === "reading" || key === "ce" || key === "comprehension ecrite" || key === "compréhension écrite (ce)") {
    subKey = "ce";
  } else if (key === "writing" || key === "ee" || key === "expression ecrite" || key === "expression écrite (ee)") {
    subKey = "ee";
  } else {
    return null;
  }

  for (const scale of TEF_SCORE_SCALE) {
    const min = scale[subKey].min;
    const max = scale[subKey].max;
    if (parsedScore >= min && parsedScore <= max) {
      return {
        nclc: scale.nclc,
        cecrl: scale.cecrl,
        range: `${min} - ${max}`,
        label: `NCLC ${scale.nclc} · ${scale.cecrl}`
      };
    }
  }

  // Under NCLC 3 fallback
  let fallbackRange = "";
  if (subKey === "co") fallbackRange = "< 116";
  if (subKey === "ce") fallbackRange = "< 97";
  if (subKey === "ee") fallbackRange = "< 145";

  return {
    nclc: "<3",
    cecrl: "A1",
    range: fallbackRange,
    label: "NCLC <3 · A1"
  };
}

export function getTargetScoreForNclc(competency: string, nclc: string): number {
  const key = competency.trim().toLowerCase();
  let subKey: "co" | "ce" | "ee";
  if (key === "listening" || key === "co" || key === "comprehension orale" || key === "compréhension orale (co)") {
    subKey = "co";
  } else if (key === "reading" || key === "ce" || key === "comprehension ecrite" || key === "compréhension écrite (ce)") {
    subKey = "ce";
  } else if (key === "writing" || key === "ee" || key === "expression ecrite" || key === "expression écrite (ee)") {
    subKey = "ee";
  } else {
    return 0;
  }

  const targetNclc = nclc.trim();
  const matched = TEF_SCORE_SCALE.find(item => item.nclc === targetNclc);
  if (matched) {
    return matched[subKey].min;
  }

  return 0;
}

export interface GoalGap {
  currentScore: number;
  targetScore: number;
  missingPoints: number;
  achieved: boolean;
  nclcGoal: string;
  cecrlGoal: string;
}

export function getScoreGapToGoal(competency: string, currentScore: number, nclcGoal: string): GoalGap {
  const targetScore = getTargetScoreForNclc(competency, nclcGoal);
  
  const matched = TEF_SCORE_SCALE.find(item => item.nclc === nclcGoal.trim());
  const cecrlGoal = matched ? matched.cecrl : "A1";

  const missingPoints = Math.max(0, targetScore - currentScore);
  const achieved = missingPoints <= 0;

  return {
    currentScore,
    targetScore,
    missingPoints,
    achieved,
    nclcGoal,
    cecrlGoal
  };
}

export interface UserGoals {
  mode: "same" | "individual";
  globalNclcGoal: string;
  goals: {
    reading: string;
    listening: string;
    writing: string;
  };
  isCustom?: boolean;
  updatedAt?: string;
}

export const DEFAULT_USER_GOALS: UserGoals = {
  mode: "same",
  globalNclcGoal: "7",
  goals: {
    reading: "7",
    listening: "7",
    writing: "7"
  }
};

export function getUserGoals(): UserGoals {
  const saved = localStorage.getItem("tef_user_goals");
  if (!saved) {
    return DEFAULT_USER_GOALS;
  }
  try {
    return { ...JSON.parse(saved), isCustom: true };
  } catch {
    return DEFAULT_USER_GOALS;
  }
}

export function getTefScore(competency: string, correct: number, total: number, customMaxScore?: number): string {
  if (!total) return "0";
  const key = competency.trim().toLowerCase();
  let maxScore = customMaxScore || 300;
  if (!customMaxScore) {
    if (key === "listening" || key === "co" || key === "comprehension orale" || key === "compréhension orale (co)") {
      maxScore = 360;
    } else if (key === "writing" || key === "ee" || key === "expression ecrite" || key === "expression écrite (ee)") {
      maxScore = 450;
    }
  }
  return Math.round((correct / total) * maxScore).toString();
}

export function getCECRL(competency: string, correct: number, total: number): string {
  const score = parseInt(getTefScore(competency, correct, total));
  const level = getTefLevelByScore(competency, score);
  return level ? level.cecrl : "A1";
}

export function getNCLC(competency: string, correct: number, total: number): string {
  const score = parseInt(getTefScore(competency, correct, total));
  const level = getTefLevelByScore(competency, score);
  return level ? level.nclc : "<3";
}
